/**
 * 绿色码头 — chapter scene for HARBOR_FREE_ROAM → MERCHANT_TURTLE_EGG → RAIN_APPROACHES
 * (owner: harbor-builder). Extends the real WorldScene; everything story-specific is data in
 * ./data and ./maps, this file only sequences beats:
 *
 *   harbor-intro  (enter at PROPHECY_REVEAL, spawn pier-start)  → gh.opening   → HARBOR_FREE_ROAM
 *   free-roam     auto commentary beats in canonical order; the 亚北 stall trigger plays the
 *                 pending beats, then gh.merchant → MERCHANT_TURTLE_EGG, then chains gh.rain
 *   rain-approach (resume at MERCHANT_TURTLE_EGG)                → gh.rain      → RAIN_APPROACHES
 *   to-inn        weather clock sultry→wind→drizzle→rain (~60 s), NPCs shelter, soft guidance;
 *                 the inn-entrance exit hands off to TurtleInn (spawn inn-entrance)
 *
 * 赵呈博 is spawned here (not through WorldScene.followers) so his Follower can be detached while a
 * cinematic walks him. Nothing here touches Math.random; timing comes from the fixed step.
 *
 * Verification drivers on `window.__nantianHarbor` (tools/verify/route.json): `converge(flag, maxMs)`
 * skips only timelines whose flag (data/timelines.ts TIMELINE_FLAG) is at or before the target, confirms
 * plain dialogues and, when idle, walks to the 亚北 stall and talks; `toInn(maxMs)` converges on
 * RAIN_APPROACHES and walks through the doorway exit. Play, skip and the drivers end in the same state.
 */
import Phaser from 'phaser';
import { Depth, FONT_BODY, FONT_TITLE, GAME_W } from '@/core/config';
import { bus, Events } from '@/core/events';
import { SceneKeys } from '@/core/scenes/keys';
import type { Facing, WeatherState } from '@/core/types';
import type { IActor, MapExit, MapInteraction } from '@/core/types/contracts';
import { flagIndex, isStoryFlag, type StoryFlagId } from '@/story/flags';
import { resolveBeat } from '@/story/StoryDirector';
import { DevFollowerTrail, WorldScene, type WorldSceneConfig } from '@/world';
import { Actor, Follower, registerDevActorAtlases } from '@/characters';
import { createDialogueRunner, hooksFromContext, type DialogueRunner } from '@/dialogue';
import { createCinematicPlayer, createSceneCinematicHost, type CinematicPlayer } from '@/cinematics';
import { getDialogueBox } from '@/ui';
import { audio } from '@/audio';
import { facingDelta } from '@/interaction';
import { registerGreenHarbor } from './register';
import { ACTOR, PORTRAIT_KEYS } from './data/actors';
import { GREEN_HARBOR_NPC_BY_DIALOGUE, GREEN_HARBOR_NPC_BY_ID, GREEN_HARBOR_NPCS } from './data/npcs';
import { BEAT_INTERACTIONS, BEAT_MARKS, BEAT_ORDER, OPENING_POS, RAIN_POS, TIMELINE_FLAG, type BeatId } from './data/timelines';
import { clockForWeather, phaseIndexAt, phasesCrossed } from './data/rainSchedule';
import { GH, GH_MAP_ID, TILE, feet } from './maps/green-harbor';
import { ensureHarborDevTextures, ensurePortraitFromDevAtlas } from './maps/devTextures';

type InteractKind = Exclude<MapInteraction['kind'], 'exit'>;

/** Debug/verification handle (see tools/verify/route.json and the harbor Playwright script). */
export interface HarborDebugHandle {
  ready: boolean;
  beat: string;
  playing(): string | null;
  dialogue(): string | null;
  busy(): boolean;
  weather(): WeatherState;
  rainClockMs(): number;
  player(): { x: number; y: number; tx: number; ty: number; facing: Facing };
  /** Position, facing and the texture key the sprite actually renders with (`actor-gh-*` once the cast sheets are loaded). */
  actor(id: string): { x: number; y: number; facing: Facing; texture: string } | null;
  npcIds(): string[];
  /** Path-walk the player to a tile (world pixels derived from feet()); resolves on arrival. */
  walkTo(tx: number, ty: number): Promise<void>;
  face(f: Facing): void;
  confirm(): void;
  /** Confirm the current map interaction (what a confirm press would do); returns its id or null. */
  interact(): string | null;
  prompt(): string | null;
  setWeather(state: WeatherState, ms?: number): void;
  /**
   * Verification driver: every ~100 ms skip the running timeline when its flag is at or before `flag`
   * (never a later one — `MERCHANT_TURTLE_EGG` leaves the chained gh.rain playing), confirm a plain
   * dialogue, or — idle before the merchant — walk to the 亚北 stall and talk. Resolves true once
   * `flag` is set and the scene is idle or only a later timeline runs; false on timeout / shutdown.
   */
  converge(flag: string, maxMs?: number): Promise<boolean>;
  /** After RAIN_APPROACHES: confirm through any dialogue, walk to the porch and through the doorway exit. Resolves true on hand-off. */
  toInn(maxMs?: number): Promise<boolean>;
}
declare global { interface Window { __nantianHarbor?: HarborDebugHandle } }

const BEAT_SET: ReadonlySet<string> = new Set(BEAT_ORDER);
const NPC_TALK_REACH = 30;
const NPC_TALK_RADIUS2 = 34 * 34;

export class GreenHarborScene extends WorldScene {
  protected readonly mapId = GH_MAP_ID;

  private runner: DialogueRunner | null = null;
  private cine: CinematicPlayer | null = null;
  private zhao: IActor | null = null;
  private follower: { destroy(): void } | null = null;
  private devTrailOwn: DevFollowerTrail | null = null;
  private followerPending = false;
  private busy = false;
  private leaving = false;
  private beat = 'handoff';
  private playingId: string | null = null;
  private rainClock = -1;
  private npcPromptId: string | null = null;
  private boxHasWait = true;
  private hint: Phaser.GameObjects.Text | null = null;
  private lastPrompt: string | null = null;
  private readonly driverStops = new Set<() => void>();
  private readonly onConfirmHarbor = (): void => { this.confirmPressed(); };
  private readonly onPromptHarbor = (p: { id?: string } | null): void => { this.lastPrompt = p?.id ?? null; };
  private waterFrame = 0;
  private waterStepAcc = 0;
  constructor() { super(SceneKeys.GREEN_HARBOR); }

  // ------------------------------------------------------------------ lifecycle
  preload(): void {
    registerGreenHarbor();
    const has = (k: string): boolean => this.textures.exists(k);
    // DEV atlases for the three portrait-bearing speakers (real art loads under ACTOR_DEFS[*].texture via the manifest).
    registerDevActorAtlases(this.load, { has, only: [ACTOR.JIANG, ACTOR.ZHAO, ACTOR.MERCHANT] });
    for (const p of Object.values(PORTRAIT_KEYS)) {
      if (has(p.key) || has(p.devAtlas)) continue;
      const file = p.devAtlas.replace(/^dev-/, '');
      this.load.atlas(p.devAtlas, `assets/dev/${file}.png`, `assets/dev/${file}.json`);
    }
  }

  protected override worldConfig(): WorldSceneConfig {
    return { player: { id: ACTOR.JIANG, actor: ACTOR.JIANG }, followers: [], defaultSpawn: 'market', weatherTransitionMs: 2500, ambiencePreset: 'harbor' };
  }

  protected override onPreCreate(): void {
    registerGreenHarbor();
    ensureHarborDevTextures(this);
    for (const p of Object.values(PORTRAIT_KEYS)) ensurePortraitFromDevAtlas(this, p.devAtlas, p.key);
    this.busy = false; this.leaving = false; this.playingId = null; this.rainClock = -1; this.npcPromptId = null; this.lastPrompt = null;
    this.follower = null; this.devTrailOwn = null; this.followerPending = false; this.hint = null;
  }

  protected override onCreate(): void {
    const ctx = this.ctx;
    const p = this.player;
    this.zhao = this.spawnActor(ACTOR.ZHAO, ACTOR.ZHAO, p.x, p.y, p.facing);

    if (ctx) {
      const box = getDialogueBox(this);
      this.boxHasWait = typeof box.waitForAdvance === 'function';
      const runner = createDialogueRunner(box, hooksFromContext(ctx), {
        host: {
          emote: (actor, emote) => { void this.actors.get(actor)?.showEmote(emote, 900); },
          face: (actor, facing) => this.actors.get(actor)?.setFacing(facing),
          sfx: (key) => audio().sfx(key),
        },
      });
      this.runner = runner;
      // A scene that shut down mid-cinematic drains the remaining steps silently: no lines, no state.
      const alive = (): boolean => this.isReady;
      const guardedRunner = {
        run: (id: string) => (alive() ? runner.run(id) : Promise.resolve()),
        isRunning: () => runner.isRunning(),
        advance: () => runner.advance(),
        get skipReadLines() { return runner.skipReadLines; },
        set skipReadLines(v: boolean) { runner.skipReadLines = v; },
        abort: () => runner.abort(),
        fastForward: (id: string) => { if (alive()) runner.fastForward(id); },
      };
      const host = createSceneCinematicHost(this, {
        dialogue: guardedRunner,
        ctx,
        state: {
          hasMark: (m) => ctx.story.hasMark(m),
          setMark: (m) => { if (alive()) ctx.story.mark(m); },
          setFlag: (f) => { if (!alive()) return; if (isStoryFlag(f)) ctx.story.advance(f); else console.warn(`[green-harbor] unknown flag "${f}"`); },
          hasFlag: (f) => isStoryFlag(f) && ctx.story.has(f),
        },
      });
      this.cine = createCinematicPlayer(host);
    }

    bus.onTyped(Events.INPUT_CONFIRM, this.onConfirmHarbor);
    bus.onTyped(Events.INTERACT_PROMPT, this.onPromptHarbor);
    this.hint = this.add.text(GAME_W - 8, 8, '', { fontFamily: FONT_BODY, fontSize: '12px', color: '#f3e6c8', stroke: '#101418', strokeThickness: 3, align: 'right' })
      .setOrigin(1, 0).setScrollFactor(0).setDepth(Depth.UI).setVisible(false);
    this.showLocationCard();
    this.installDebugHandle();

    const rf = this.entry.resumeFlag ?? ctx?.story.current() ?? null;
    const res = resolveBeat('GreenHarbor', rf);
    this.beat = res.handoff && ctx?.story.has('RAIN_APPROACHES') ? 'to-inn' : res.beat;
    // Cinematic beats hold `busy` + the input lock from the very first fixed step, so no auto
    // interaction under the enforced start position can steal the beat (the rain start sits in one).
    if (this.beat === 'harbor-intro' || this.beat === 'rain-approach') { this.busy = true; this.setInputLocked(true); }
    void this.startBeat();
  }

  protected override onShutdown(): void {
    for (const stop of [...this.driverStops]) stop();
    this.driverStops.clear();
    bus.offTyped(Events.INPUT_CONFIRM, this.onConfirmHarbor);
    bus.offTyped(Events.INTERACT_PROMPT, this.onPromptHarbor);
    this.runner?.abort();
    this.detachFollower();
    this.zhao = null;
    this.hint = null;
    if (window.__nantianHarbor) { window.__nantianHarbor.ready = false; delete window.__nantianHarbor; }
  }

  /** Weather changes from a draining cinematic must not touch a dead scene / the shared context. */
  override setWeather(state: WeatherState, transitionMs?: number): void {
    if (!this.isReady) return;
    super.setWeather(state, transitionMs);
  }

  // ------------------------------------------------------------------ beats
  private async startBeat(): Promise<void> {
    const ctx = this.ctx;
    if (!this.zhao) return;
    switch (this.beat) {
      case 'harbor-intro': {
        // Fresh from the prologue: 赵呈博 stands out on the pier looking at the sea; 江子潮 walks up beside him.
        this.player.setPosition(OPENING_POS.jiangStart.x, OPENING_POS.jiangStart.y, OPENING_POS.jiangStart.facing);
        this.zhao.setPosition(OPENING_POS.zhao.x, OPENING_POS.zhao.y, OPENING_POS.zhao.facing);
        this.cameraFollowPlayer();
        await this.heldBeat(async () => { await this.delay(450); await this.playTimeline('gh.opening'); });
        this.attachFollower(true);
        break;
      }
      case 'free-roam':
        this.attachFollower();
        break;
      case 'rain-approach': {
        // Resume: the merchant exchange is done; the rain beat restarts from its beginning.
        this.player.setPosition(RAIN_POS.jiang.x, RAIN_POS.jiang.y, RAIN_POS.jiang.facing);
        this.zhao.setPosition(RAIN_POS.zhao.x, RAIN_POS.zhao.y, RAIN_POS.zhao.facing);
        this.cameraFollowPlayer();
        await this.heldBeat(async () => { await this.delay(450); await this.runRainBeat(); });
        break;
      }
      case 'to-inn':
        this.attachFollower();
        this.startRainClock();
        break;
      default:
        // Not our flag: let the route resolver place the player (never soft-lock on a stale entry).
        if (ctx) await ctx.enterRoute();
    }
  }

  /** Runs an entry beat while `busy` + the input lock are already held (see onCreate); releases both. */
  private async heldBeat(fn: () => Promise<void>): Promise<void> {
    try { await fn(); }
    finally { this.busy = false; if (this.isReady) this.setInputLocked(false); }
  }

  private async withBusy<T>(fn: () => Promise<T>): Promise<T | undefined> {
    if (this.busy) return undefined;
    this.busy = true;
    this.npcPromptId = null;
    try { return await fn(); }
    finally { this.busy = false; }
  }

  private async playTimeline(id: string): Promise<'completed' | 'skipped'> {
    if (!this.cine || !this.isReady) return 'completed';
    this.playingId = id;
    try { return await this.cine.play(id); }
    finally { this.playingId = null; }
  }

  /** Plays every commentary beat up to `beat` that has not been seen, in canonical order (P9). */
  private async playBeatsUpTo(beat: BeatId): Promise<void> {
    const story = this.ctx?.story;
    if (!story) return;
    if (this.runner?.isRunning()) await this.delay(100);
    const upto = BEAT_ORDER.indexOf(beat);
    for (let i = 0; i <= upto; i++) {
      const b = BEAT_ORDER[i] as BeatId;
      if (story.hasMark(BEAT_MARKS[b])) continue;
      story.mark(`done:${BEAT_INTERACTIONS[b]}`);
      await this.playTimeline(b);
      if (!this.isReady) return;
    }
  }

  private async runRainBeat(): Promise<void> {
    this.detachFollower();
    await this.playTimeline('gh.rain');
    if (!this.isReady) return;
    await this.showInnFarView();
    if (!this.isReady) return;
    this.attachFollower(true);
    this.startRainClock();
  }

  /**
   * P12's first line (“远远就能看见大海龟旅店……”) follows “一会儿就下雨了” directly, so it plays right
   * after gh.rain — played or skipped — as a plain dialogue; the `obs-inn-far` auto rect (map data)
   * only remains as the fallback for a resume at RAIN_APPROACHES that never saw it (same `done:` mark).
   */
  private async showInnFarView(): Promise<void> {
    const story = this.ctx?.story;
    if (!story || !this.isReady || story.hasMark('done:obs-inn-far')) return;
    story.mark('done:obs-inn-far');
    await this.runDialogue('gh.obs.inn-far');
  }

  private async runDialogue(id: string): Promise<void> {
    if (!this.runner || !this.isReady) return;
    this.setInputLocked(true);
    try { await this.runner.run(id); }
    finally { if (this.isReady) this.setInputLocked(false); }
  }

  // ------------------------------------------------------------------ interactions
  protected override onInteract(kind: InteractKind, i: MapInteraction): void {
    if (this.busy || this.leaving || !this.isReady) return;
    if (kind === 'trigger' && i.cinematic) { void this.withBusy(() => this.runTrigger(i.cinematic as string)); return; }
    if (!i.dialogue) return;
    const dialogue = i.dialogue;
    if (kind === 'talk') {
      const npc = GREEN_HARBOR_NPC_BY_DIALOGUE[dialogue];
      const a = npc ? this.actors.get(npc.id) : null;
      a?.faceTowards(this.player.x, this.player.y);
    }
    void this.withBusy(() => this.runDialogue(dialogue));
  }

  private async runTrigger(id: string): Promise<void> {
    if (BEAT_SET.has(id)) { await this.playBeatsUpTo(id as BeatId); return; }
    if (id === 'gh.merchant') {
      // Story order: 赵呈博's 亚北 explanation precedes "嗨！" — play any beat the player skipped past first.
      await this.playBeatsUpTo('gh.beat.leaves');
      if (!this.isReady) return;
      await this.playTimeline('gh.merchant');
      if (!this.isReady) return;
      await this.runRainBeat();
      return;
    }
    await this.playTimeline(id);
  }

  protected override onExit(exit: MapExit | null): void {
    if (!exit || exit.id !== 'inn-entrance' || this.leaving || this.busy || !this.isReady) return;
    const story = this.ctx?.story;
    if (!story || !story.has('RAIN_APPROACHES')) return; // before the rain the doorway is only a look (inn-door-early)
    this.leaving = true;
    story.mark('seen:enter-inn');
    this.hint?.setVisible(false);
    void this.goToExit(exit).then((ok) => { if (!ok && this.isReady) { this.leaving = false; this.setInputLocked(false); } });
  }

  /** Confirm while no map interaction is in front: talk to the nearest environment NPC. */
  private confirmPressed(): void {
    if (!this.isReady) return;
    if (this.runner?.isRunning()) { if (!this.boxHasWait) this.runner.advance(); return; }
    if (this.busy || this.inputLocked || this.leaving) return;
    if (this.currentInteraction) return; // WorldScene.interact() handles map interactions
    const cand = this.nearestNpcInFront();
    if (!cand) return;
    const dlg = GREEN_HARBOR_NPC_BY_ID[cand.id]?.placement.dialogue;
    if (!dlg) return;
    cand.actor.faceTowards(this.player.x, this.player.y);
    void this.withBusy(() => this.runDialogue(dlg));
  }

  private nearestNpcInFront(): { id: string; actor: IActor } | null {
    const p = this.player;
    const d = facingDelta(p.facing);
    const probe = { x: p.x + d.dx * NPC_TALK_REACH, y: p.y - 6 + d.dy * NPC_TALK_REACH };
    let best: { id: string; actor: IActor } | null = null;
    let bestD = NPC_TALK_RADIUS2;
    for (const n of GREEN_HARBOR_NPCS) {
      if (n.id === ACTOR.MERCHANT) continue; // the stall rects own his prompts (main line + flavour)
      const a = this.actors.get(n.id);
      if (!a) continue;
      const dx = a.x - probe.x, dy = a.y - 8 - probe.y;
      const dd = dx * dx + dy * dy;
      if (dd < bestD) { bestD = dd; best = { id: n.id, actor: a }; }
    }
    return best;
  }

  // ------------------------------------------------------------------ fixed step: NPC prompt, follower, rain clock
  protected override onFixedStep(dt: number): void {
    this.devTrailOwn?.tick(dt);

    // 6 fps water tileset swap (gh-tiles ↔ gh-tiles-b)
    this.waterStepAcc += dt;
    if (this.waterStepAcc >= 166.67) {
      this.waterStepAcc -= 166.67;
      this.waterFrame = 1 - this.waterFrame;
      const texKey = this.waterFrame === 0 ? 'gh-tiles' : 'gh-tiles-b';
      if (this.textures.exists(texKey)) {
        const layer = this.mapRuntime?.layers?.[0];
        if (layer?.tileset) {
          const img = this.textures.get(texKey).getSourceImage();
          if (img) (layer.tileset as unknown as { setImage(img: unknown): void }).setImage?.(img);
        }
      }
    }

    if (this.busy || this.inputLocked || this.leaving) { this.npcPromptId = null; }
    else if (this.currentInteraction) { this.npcPromptId = null; } // the interaction system owns the prompt
    else {
      const cand = this.nearestNpcInFront();
      const id = cand ? `npc:${cand.id}` : null;
      if (id !== this.npcPromptId) {
        this.npcPromptId = id;
        bus.emitTyped(Events.INTERACT_PROMPT, id && cand ? { label: `与${GREEN_HARBOR_NPC_BY_ID[cand.id]?.name ?? ''}交谈`, kind: 'talk', id } : null);
      }
    }

    if (this.rainClock >= 0 && !this.busy && !this.leaving) {
      const prev = this.rainClock;
      this.rainClock += dt;
      for (const ph of phasesCrossed(prev, this.rainClock)) {
        this.setWeather(ph.state, ph.transitionMs);
        if (ph.state === 'drizzle') void this.onDrizzle();
        // Autosave the phase (+ position): a reload resumes the clock from the saved weather
        // (clockForWeather) instead of restarting the ~60 s sequence from wind.
        if (this.ctx) { this.writePosition(true); this.ctx.autosave(); }
      }
    }
  }

  private startRainClock(): void {
    if (this.rainClock >= 0) return;
    const w = this.weather;
    this.rainClock = clockForWeather(w);
    if (w === 'sultry') this.setWeather('wind', 4000);
    if (phaseIndexAt(this.rainClock) >= 1) this.showHint();
  }

  /** Soft guidance once the drizzle starts: the inn paragraph's crowd behaviour (verbatim) + a HUD pointer. */
  private async onDrizzle(): Promise<void> {
    this.showHint();
    const story = this.ctx?.story;
    if (!story || story.hasMark('seen:rain-hint')) return;
    await this.delay(900);
    if (!this.isReady || this.busy || this.leaving) return;
    await this.withBusy(() => this.runDialogue('gh.rain.hint'));
  }

  private showHint(): void {
    this.hint?.setText('雨来了 — 往东，去大海龟旅店躲一躲').setVisible(true);
  }

  // ------------------------------------------------------------------ 赵呈博 follower
  private attachFollower(walkFirst = false): void {
    const z = this.zhao, p = this.player;
    if (this.follower || !z || !this.isReady) return;
    if (walkFirst && z instanceof Actor) {
      const d = facingDelta(p.facing);
      const seed = { x: p.x - d.dx * 28, y: p.y - d.dy * 28 };
      if (Math.hypot(z.x - seed.x, z.y - seed.y) > 20 && !this.isBlocked(seed.x, seed.y - 6)) {
        this.followerPending = true;
        void z.walkTo(seed.x, seed.y, 92).then(() => { if (this.followerPending && this.isReady) { this.followerPending = false; this.attachFollower(false); } });
        return;
      }
    }
    this.followerPending = false;
    if (z instanceof Actor && p instanceof Actor) {
      const fo = new Follower(this, z, p, { slot: 1 });
      this.follower = { destroy: () => fo.destroy() };
    } else {
      const trail = new DevFollowerTrail(p, [z], { spacingPx: 28, speed: 100 });
      this.devTrailOwn = trail;
      this.follower = { destroy: () => { trail.destroy(); if (this.devTrailOwn === trail) this.devTrailOwn = null; } };
    }
  }

  private detachFollower(): void {
    this.followerPending = false;
    if (this.follower) { try { this.follower.destroy(); } catch { /* ignore */ } this.follower = null; }
    try { this.zhao?.stop(); } catch { /* ignore */ }
  }

  // ------------------------------------------------------------------ presentation helpers
  private showLocationCard(): void {
    const title = this.add.text(GAME_W / 2, 30, '绿色码头', { fontFamily: FONT_TITLE, fontSize: '22px', color: '#f3e6c8', stroke: '#101418', strokeThickness: 4 })
      .setOrigin(0.5).setScrollFactor(0).setDepth(Depth.UI);
    const sub = this.add.text(GAME_W / 2, 52, '南陆最北的码头', { fontFamily: FONT_BODY, fontSize: '12px', color: '#e2c48c', stroke: '#101418', strokeThickness: 3 })
      .setOrigin(0.5).setScrollFactor(0).setDepth(Depth.UI);
    this.tweens.add({ targets: [title, sub], alpha: 0, delay: 2200, duration: 700, onComplete: () => { title.destroy(); sub.destroy(); } });
  }

  private delay(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      const onShutdown = (): void => { timer.remove(false); resolve(); };
      const timer = this.time.delayedCall(ms, () => { this.events.off(Phaser.Scenes.Events.SHUTDOWN, onShutdown); resolve(); });
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, onShutdown);
    });
  }

  // ------------------------------------------------------------------ verification drivers (route.json)
  /**
   * Polls `step` every 100 ms until it returns a result or `maxMs` passes; a scene shutdown resolves
   * `onStop()` (so a hand-off to the inn counts as success for toInn). Never throws.
   */
  private drive(maxMs: number, step: () => boolean | undefined, onStop: () => boolean): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const t0 = performance.now();
      let timer: ReturnType<typeof setTimeout> | null = null;
      const stop = (): void => { if (timer !== null) clearTimeout(timer); timer = null; this.driverStops.delete(stop); resolve(onStop()); };
      this.driverStops.add(stop);
      const finish = (ok: boolean): void => { this.driverStops.delete(stop); timer = null; resolve(ok); };
      const tick = (): void => {
        timer = null;
        if (!this.isReady) { finish(onStop()); return; }
        if (performance.now() - t0 > maxMs) { finish(false); return; }
        let r: boolean | undefined;
        try { r = step(); } catch (e) { console.warn('[green-harbor] driver step failed', e); }
        if (r !== undefined) { finish(r); return; }
        timer = setTimeout(tick, 100);
      };
      tick();
    });
  }

  private pressConfirm(): void { bus.emitTyped(Events.INPUT_CONFIRM, { source: 'keyboard' }); }

  private converge(flag: string, maxMs: number): Promise<boolean> {
    if (!isStoryFlag(flag)) return Promise.resolve(false);
    const target = flagIndex(flag);
    return this.drive(maxMs, () => {
      const story = this.ctx?.story;
      if (!story) return false;
      const playing = this.playingId;
      const playingFlag: StoryFlagId | undefined = playing ? TIMELINE_FLAG[playing] : undefined;
      const playingIdx = playingFlag ? flagIndex(playingFlag) : -1;
      const reached = story.has(flag);
      if (reached && (!this.busy || playingIdx > target)) return true;
      if (playing && playingIdx >= 0 && playingIdx <= target) { this.cine?.skip(); return undefined; }
      if (this.runner?.isRunning()) { this.pressConfirm(); return undefined; }
      if (!reached && !this.busy && !this.inputLocked && !this.leaving) this.driveStep(flag);
      return undefined;
    }, () => false);
  }

  /** Idle before the merchant: walk to the stall front (auto beats interrupt the walk; the loop resumes it), face it, talk. */
  private driveStep(target: StoryFlagId): void {
    const story = this.ctx?.story;
    if (!story || this.player.isMoving()) return;
    if (target === 'MERCHANT_TURTLE_EGG' && story.current() === 'HARBOR_FREE_ROAM') {
      const f = feet(GH.merchantTalk.x + 2, GH.merchantTalk.y + 1);
      if (Math.abs(this.player.x - f.x) > 6 || Math.abs(this.player.y - f.y) > 6) { void this.player.walkTo(f.x, f.y); return; }
      if (this.player.facing !== 'up') { this.player.setFacing('up'); return; } // the interaction system re-selects on the next fixed step
      if (this.currentInteraction?.id === 'talk-merchant') this.interact();
    }
  }

  private toInn(maxMs: number): Promise<boolean> {
    const t0 = performance.now();
    let stage = 0;
    const door = feet(GH.innDoor.x + 1, GH.innDoor.y + 1);            // porch tile in front of the doorway (52,33)
    const porch = feet(GH.innDoor.x + 1, GH.innDoor.y + 2);           // (52,34)
    const inside = { x: door.x, y: GH.innDoor.y * TILE + TILE / 2 };  // doorway tile centre: crossing into the exit rect fires it
    const near = (p: { x: number; y: number }): boolean => Math.abs(this.player.x - p.x) <= 6 && Math.abs(this.player.y - p.y) <= 6;
    return this.converge('RAIN_APPROACHES', maxMs).then((ok) => {
      if (!ok) return false;
      return this.drive(Math.max(1000, maxMs - (performance.now() - t0)), () => {
        if (this.leaving) return undefined;                          // router transition in flight → shutdown resolves true
        if (this.busy) { if (this.runner?.isRunning()) this.pressConfirm(); return undefined; }
        if (this.inputLocked || this.player.isMoving()) return undefined;
        if (stage === 0) { stage = 1; if (!near(porch)) { void this.player.walkTo(porch.x, porch.y); return undefined; } }
        if (stage === 1) { stage = 2; if (!near(door)) { void this.player.walkTo(door.x, door.y); return undefined; } }
        if (stage === 2) { stage = 3; void this.player.walkTo(inside.x, inside.y); return undefined; }
        if (!near(door) && !near(inside)) stage = 0;                 // drifted (an interruption mid-walk): start over
        return undefined;
      }, () => this.leaving);
    });
  }

  private installDebugHandle(): void {
    const handle: HarborDebugHandle = {
      ready: true,
      beat: this.beat,
      playing: () => this.playingId,
      dialogue: () => this.runner?.currentDialogue() ?? null,
      busy: () => this.busy,
      weather: () => this.weather,
      rainClockMs: () => this.rainClock,
      player: () => ({ x: this.player.x, y: this.player.y, tx: Math.floor(this.player.x / TILE), ty: Math.floor((this.player.y - 1) / TILE), facing: this.player.facing }),
      actor: (id) => { const a = this.actors.get(id); return a ? { x: a.x, y: a.y, facing: a.facing, texture: a.sprite.texture.key } : null; },
      npcIds: () => GREEN_HARBOR_NPCS.map((n) => n.id),
      walkTo: (tx, ty) => { const f = feet(tx, ty); return this.player.walkTo(f.x, f.y); },
      face: (f) => this.player.setFacing(f),
      confirm: () => bus.emitTyped(Events.INPUT_CONFIRM, { source: 'keyboard' }),
      interact: () => this.interact()?.id ?? null,
      prompt: () => this.lastPrompt,
      setWeather: (s, ms) => this.setWeather(s, ms),
      converge: (flag, maxMs = 20000) => this.converge(flag, maxMs),
      toInn: (maxMs = 20000) => this.toInn(maxMs),
    };
    window.__nantianHarbor = handle;
    // keep `beat` current once startBeat resolved the entry
    Object.defineProperty(handle, 'beat', { get: () => this.beat, enumerable: true });
  }
}

/** Landmarks exported for tooling (tile coords → world px), e.g. the verification script. */
export const HARBOR_LANDMARKS = {
  merchantTalk: feet(GH.merchantTalk.x + 2, GH.merchantTalk.y + 1),
  innDoor: feet(GH.innDoor.x + 1, GH.innDoor.y + 1),
} as const;
